import React, { memo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Container,
  FileUpload,
  Header,
  SegmentedControl,
  SpaceBetween,
  Textarea,
} from '@amzn/awsui-components-core';

import { BatchUnpublishPageViewModel } from './BatchUnpublishPageViewModel';

interface InputStepViewProps {
  readonly vm: BatchUnpublishPageViewModel;
}

type InputMode = 'text' | 'file';

export const InputStepView = memo(function InputStepView({ vm }: InputStepViewProps) {
  const [inputMode, setInputMode] = useState<InputMode>('text');
  const [files, setFiles] = useState<File[]>([]);

  return (
    <Container header={<Header variant="h2">Batch Unpublish</Header>}>
      <SpaceBetween size="m">
        <Box variant="p">
          Provide text that contains the content UUIDs you want to unpublish. The tool will
          automatically extract all UUIDs from your input — you can use any format (a plain list, a
          spreadsheet paste, URLs, notes, etc.) as long as the content IDs are present somewhere in
          the text.
        </Box>

        <SegmentedControl
          selectedId={inputMode}
          onChange={({ detail }) => setInputMode(detail.selectedId as InputMode)}
          options={[
            { id: 'text', text: 'Paste text' },
            { id: 'file', text: 'Upload file' },
          ]}
        />

        {inputMode === 'text' && (
          <Textarea
            value={vm.rawText}
            onChange={({ detail }) => vm.setRawText(detail.value)}
            placeholder="Paste text containing content UUIDs here..."
            rows={12}
          />
        )}

        {inputMode === 'file' && (
          <FileUpload
            accept=".txt,.csv"
            value={files}
            onChange={({ detail }) => {
              setFiles(detail.value);
              const file = detail.value[0];
              if (file) vm.handleFileUpload(file);
            }}
            i18nStrings={{
              uploadButtonText: () => 'Choose file',
              dropzoneText: () => 'Drop file here',
              removeFileAriaLabel: (_i, name) => `Remove ${name}`,
            }}
            constraintText="Accepts .txt or .csv files"
          />
        )}

        {vm.inputError && <Alert type="error">{vm.inputError}</Alert>}

        <Button
          variant="primary"
          disabled={vm.rawText.trim().length === 0}
          onClick={vm.submitInput}
        >
          Parse &amp; Validate
        </Button>
      </SpaceBetween>
    </Container>
  );
});
